import React from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { useFormik } from 'formik';
import cn from 'classnames';

import { Button, FormInputPin } from 'modules/common/input';

import { useAuthOtp, useSelectAuthMethod } from '../hooks';
import { multiFactorAuthSchema } from '../validation';

const INIT_FORM = {
  otp: '',
};

export const METHOD_OPTS = [
  {
    label: 'Via SMS',
    value: 'SMS',
    label2: 'SMS',
    description: 'Use your phone number to access your account.',
  },
  {
    label: 'Via Mail',
    value: 'EMAIL',
    label2: 'Email',
    description: 'Use your email address to access your account.',
  },
  // {
  //   label: 'Via App',
  //   value: 'APP',
  //   description: 'Use an app to access your account.',
  // },
];

function MultiFactorAuthForm() {
  const [selectMethod, setSelectMethod] = React.useState();
  const [validation, setValidation] = React.useState(false);

  const history = useHistory();
  const location = useLocation();

  const [isPostLoading, post] = useSelectAuthMethod();
  const [isOtpLoading, postOtp] = useAuthOtp();

  const { submitForm, setFieldValue, values, errors } = useFormik({
    initialValues: INIT_FORM,
    validationSchema: multiFactorAuthSchema,
    validateOnChange: validation,
    validateOnBlur: validation,
    onSubmit: (payload) => {
      const args = {
        payload: {
          ...payload,
          user_uuid: location?.user_uuid,
        },
        userType: location?.userType,
      };

      postOtp(args, (_show_tutorial) => {
        if (_show_tutorial) {
          history.push('/onboarding');
          return;
        }
        history.push('/');
      });
    },
  });

  const handleOnClick = React.useCallback(
    (row) => () => {
      const args = {
        payload: {
          method: row?.value,
          user_uuid: location?.user_uuid,
        },
        userType: location?.userType,
      };

      post(args, (res) => {
        const { receiver_start, receiver_end } = res?.response || {};

        setSelectMethod({
          ...row,
          receiver_start,
          receiver_end,
        });
      });
    },
    [post, location?.user_uuid, location?.userType]
  );

  React.useEffect(() => {
    if (location?.isAllowed) {
      if (location?.method) {
        setSelectMethod({
          ...METHOD_OPTS?.find((item) => item?.value === location?.method),
          receiver_start: location?.receiver_start,
          receiver_end: location?.receiver_end,
        });
      }

      return;
    }

    history.push('/login');
  }, [
    handleOnClick,
    history,
    location?.isAllowed,
    location?.method,
    location?.receiver_start,
    location?.receiver_end,
  ]);

  if (!location?.isAllowed) return null;

  if (!selectMethod) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">
          Please, select an authentication to proceed.
        </div>

        <div className="grid grid-cols-2 gap-2">
          {METHOD_OPTS?.map((item, key) => (
            <Button
              // eslint-disable-next-line react/no-array-index-key
              key={key}
              label={
                <div className="space-y-3">
                  <div className="font-semibold">{item?.label}</div>
                  <div className="text-gray-500 text-xs">
                    {item?.description}
                  </div>
                </div>
              }
              onClick={handleOnClick(item)}
              className={cn(
                'w-full h-28 border rounded p-5 hover:bg-gray-100 focus:bg-gray-100',
                {
                  'bg-gray-200 bg-opacity-60': item?.value === location?.method,
                }
              )}
              disabled={isPostLoading}
            />
          ))}
        </div>

        <div className="flex flex-col items-center space-y-2 pt-2">
          <div>
            <CgSpinner
              className={cn('h-5 w-5 animate-spin', {
                'opacity-0': !isPostLoading,
              })}
            />
          </div>
          <div>
            <Button
              onClick={() => history.push(location?.goBack)}
              className="text-sm font-medium text-black hover:underline"
              label="Go back"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {/* <FormInputNumber
          label="OTP"
          name="otp"
          placeholder="Enter the 6-digit code"
          value={values?.otp || ''}
          autoComplete="none"
          onSetFieldValue={setFieldValue}
          error={errors?.otp}
          required
        /> */}

        <div className="space-y-3">
          <h2 className="font-light text-sm text-gray-500">
            Enter 6-digit OTP
          </h2>
          <FormInputPin
            label="OTP"
            name="otp"
            error={errors?.otp}
            value={values?.otp}
            onSetFieldValue={setFieldValue}
            required
            pinClassName="w-24 h-[70px] rounded-md text-gray-800 border text-3xl font-bold"
            inActivePinClassName="border-gray-300 bg-gray-50"
            activePinClassName="border-primary-500 ring-2 ring-primary-500 bg-white"
          />
        </div>

        <p className="text-sm space-y-2.5">
          <div>
            Code sent to{' '}
            <span className="font-semibold">
              {selectMethod?.receiver_start}
              {selectMethod?.value === 'SMS' ? '*******' : '***********'}
              {selectMethod?.receiver_end}
            </span>{' '}
            via <span className="font-semibold">{selectMethod?.label2}</span>.
            It might take a couple of minutes for your code to arrive.
          </div>

          <div>
            Didn&apos;t get the code?
            <Button
              label="Resend code"
              className="btn font-semibold xs !text-sm !p-0 -mt-1 ml-1"
              onClick={handleOnClick(selectMethod)}
              disabled={isPostLoading}
            />
          </div>
        </p>
      </div>

      <div className="space-y-3 text-center">
        <div>
          <Button
            onClick={() => {
              submitForm();
              setValidation(true);
            }}
            withLoader
            isLoading={isOtpLoading}
            className="btn primary md w-full"
            label="Submit"
          />
        </div>
        <div>
          <Button
            onClick={() => {
              if (location?.method) {
                history.push(location?.goBack);
                return;
              }
              setSelectMethod(null);
            }}
            className="text-sm font-medium text-black hover:underline"
            label="Go back"
          />
        </div>
      </div>
    </div>
  );
}
MultiFactorAuthForm.defaultProps = {};

MultiFactorAuthForm.propTypes = {};

export default MultiFactorAuthForm;
